import { Flex } from '@/app/components/Flex/Flex'
import { Icon } from '@/app/components/Icon/Icon'
import { Text } from '@/app/components/Text/Text'
import { UI } from '@/app/components/UI/UI'
import type { FunctionalComponent } from 'preact'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper/types'
import styles from './index.module.scss'

type TarotTopic = {
  id: string
  title: string
  questions: string[]
}

const tarotTopics: TarotTopic[] = [{
  id: 'common',
  title: 'Общее',
  questions: [
    '🔮 Что со мной сейчас происходит?',
    '⚡ Какая энергия меня окружает?',
    '🌀 Что меня тормозит?',
    '🎯 На чём мне сфокусироваться?',
    '🌅 Что ждёт меня впереди?',
    '🃏 Что карты хотят мне сказать?',
    '🌙 Чего я пока не вижу?',
  ],
}, {
  id: 'love',
  title: 'Любовь',
  questions: [
    '❤️ Что ждёт мои отношения?',
    '🤔 Можно ли ему доверять?',
    '💑 Есть ли у нас будущее?',
    '💘 Когда я встречу любовь?',
    '🔥 Откуда это напряжение?',
    '🌹 Что он ко мне чувствует?',
    '💔 Стоит ли бороться за нас?',
  ],
}, {
  id: 'career',
  title: 'Карьера',
  questions: [
    '📈 Куда движется моя карьера?',
    '💵 Что меня ждёт с деньгами?',
    '🚀 Пора ли менять работу?',
    '🤝 Надёжен ли этот партнёр?',
    '🏆 Что приведёт меня к успеху?',
    '🧱 Что тормозит мой рост?',
    '💼 Браться ли за это дело?',
  ],
}, {
  id: 'self',
  title: 'Саморазвитие',
  questions: [
    '🧭 Какой путь сейчас мой?',
    '🪞 Что мне принять в себе?',
    '🌱 Что мне пора отпустить?',
    '💡 Какой урок я прохожу?',
    '🕊️ Как обрести покой?',
    '🔥 В чём моя настоящая сила?',
    '🌑 Что я в себе прячу?',
  ],
}, {
  id: 'situation',
  title: 'Ситуация',
  questions: [
    '⚖️ Какое решение верное?',
    '👁️ Чего я здесь не вижу?',
    '🔑 Как разрешить конфликт?',
    '📩 Соглашаться ли мне?',
    '🌊 Как выйти из ситуации?',
    '🧩 Чем всё это закончится?',
    '🎭 Что от меня скрывают?',
  ],
}]

type TarotSectionsProps = {
  initialGroupId?: string
  onGroupChange?: (groupId: string) => void
  onQuestionClick: (topic: TarotTopic, question: string) => void
  onClose: () => void
}

export const TarotSections: FunctionalComponent<TarotSectionsProps> = ({
  initialGroupId,
  onGroupChange,
  onQuestionClick,
  onClose,
}) => {
  const swiperRef = useRef<SwiperType | null>(null)
  const initialIdx = useMemo(
    () => Math.max(0, tarotTopics.findIndex(topic => topic.id === initialGroupId)),
    [initialGroupId],
  )
  const [activeIdx, setActiveIdx] = useState(initialIdx)

  useEffect(() => {
    setActiveIdx(initialIdx)

    if (swiperRef.current && swiperRef.current.activeIndex !== initialIdx) {
      swiperRef.current.slideTo(initialIdx, 0)
    }
  }, [initialIdx])

  return (
    <>
      <div
        class={styles.overlay}
        onClick={onClose}
      />

      <UI.Panel
        shadow
        radius='m'
        class={styles.root}
      >
        <UI.Tabs>
          {tarotTopics.map((topic, idx) => (
            <UI.Tab
              key={topic.id}
              height={47}
              active={activeIdx === idx}
              onClick={() => {
                setActiveIdx(idx)
                onGroupChange?.(topic.id)
                swiperRef.current?.slideTo(idx)
              }}
            >
              {topic.title}
            </UI.Tab>
          ))}
        </UI.Tabs>

        <div class={styles.swiperWrapper}>
          <Swiper
            initialSlide={initialIdx}
            slidesPerView={1}
            onSwiper={(swiper) => {
              swiperRef.current = swiper

              if (swiper.activeIndex !== initialIdx) {
                swiper.slideTo(initialIdx, 0)
              }
            }}
            onSlideChange={(swiper) => {
              setActiveIdx(swiper.activeIndex)
              const topic = tarotTopics[swiper.activeIndex]

              if (topic) {
                onGroupChange?.(topic.id)
              }
            }}
          >
            {tarotTopics.map(topic => (
              <SwiperSlide key={topic.id} class={styles.swiperSlide}>
                <div class={styles.table}>
                  {topic.questions.map(question => (
                    <button
                      key={question}
                      type='button'
                      class={styles.row}
                      onClick={() => {
                        onQuestionClick(topic, question)
                      }}
                    >
                      <Flex align='center' gap={8} dir='row'>
                        <Icon>
                          {getQuestionEmoji(question)}
                        </Icon>

                        <Text
                          fontSize={16}
                          class={styles.name}
                        >
                          {getQuestionText(question)}
                        </Text>
                      </Flex>
                    </button>
                  ))}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </UI.Panel>
    </>
  )
}

function getQuestionEmoji(question: string) {
  return question.split(' ')[0] ?? ''
}

function getQuestionText(question: string) {
  return question.split(' ').slice(1).join(' ')
}
